import { useBeacon } from '@api/beacon'
import { user } from '@api/swagger'
import { validateEmail } from '@helpers/validateEmail'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import { useMountEffect } from '@hooks/useMountEffect'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate } from 'react-router'
import CountryInformation from '../Card/CountryInformation'
import { useAuth } from '../Routes/AuthProvider'
import LoginSkeleton from '../Skeleton/LoginSkeleton'
import RedirectingSkeleton from '../Skeleton/RedirectingSkeleton'
import AuthFormCard from './AuthFormCard'
import { isSsoHandoffRequest } from '../../utils/isSsoHandoffRequest'

const Login: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { handleError } = useAppSnackbar()
  const [loginError, setLoginError] = useState('')
  useBeacon(process.env.REACT_APP_HELP_SCOUT_BEACON_LOGIN_ID)

  const location = useLocation()
  const auth = useAuth()
  const { defaultAccountPath, isLoggedIn, isInitialized } = auth
  const discourseUrl = useRef<string>()

  const fetchDiscourseRedirectUrlErrorMessage = t(
    'Login.FetchDiscourseRedirectUrl.Error',
    'An error occurred while attempting to fetch the Discourse redirect URL.'
  )

  const fetchDiscourseRedirectUrl = async (
    sso: string,
    sig: string,
    returnUrl: string
  ): Promise<void> => {
    discourseUrl.current = undefined
    try {
      const { redirectTo } = await user.fetchDiscourseConnectRedirectUrl({
        userId: 'me',
        body: {
          sso,
          sig,
          returnURL: !!returnUrl ? returnUrl : undefined,
        },
      })

      discourseUrl.current = redirectTo
    } catch (err) {
      await handleError(err, fetchDiscourseRedirectUrlErrorMessage)
    }
  }

  const [showCountryInformationForm, setShowCountryInformationForm] =
    useState(false)
  const [redirectingToInteraction, setRedirectingToInteraction] =
    useState(false)

  const params = new URLSearchParams(location.search)
  const sso = params.get('sso') ?? ''
  const sig = params.get('sig') ?? ''
  const returnUrl = params.get('ReturnURL') ?? ''
  const interactionUid = params.get('interaction_uid') ?? ''
  const hasInteractionUid = interactionUid !== ''
  const isHandoff = isSsoHandoffRequest(location.search)

  const { from, hasChangedPassword } = location.state ?? {
    hasChangedPassword: false,
  }

  /**
   * When AuthProvider finishes its initial GET /session, redirect already
   * logged in users away from /login. AuthProvider populates isLoggedIn from
   * the same session response, so we read from context instead of refetching.
   * Runs once on mount; subsequent isLoggedIn changes (from handleLogin) are
   * left to handleLogin so it can redirect to the OIDC interaction URL.
   */
  useMountEffect(() => {
    if (!isInitialized || !isLoggedIn) return

    if (hasInteractionUid) {
      window.location.replace(
        `${process.env.REACT_APP_API_BASE_URL}/interaction/${encodeURIComponent(
          interactionUid
        )}`
      )
      return
    }

    if (sso && sig) {
      fetchDiscourseRedirectUrl(sso, sig, returnUrl).then(() => {
        if (discourseUrl.current) {
          window.location.replace(discourseUrl.current)
        } else {
          navigate(from ?? { pathname: defaultAccountPath }, { replace: true })
        }
      })
      return
    }

    navigate(from ?? { pathname: defaultAccountPath }, { replace: true })
  })

  const handleLogin = async (email: string, password: string) => {
    const isEmailValid = validateEmail(email)

    const signinResult = isEmailValid
      ? await auth.signin({
          email,
          password,
        })
      : undefined

    if (signinResult) {
      const { session, landingPath } = signinResult
      if (hasInteractionUid) {
        setRedirectingToInteraction(true)
        window.location.replace(
          `${
            process.env.REACT_APP_API_BASE_URL
          }/interaction/${encodeURIComponent(interactionUid)}`
        )
        return
      }

      const countryOfCitizenship = session.countryOfCitizenship ?? ''
      const countryOfResidence = session.countryOfResidence ?? ''
      const hasCountryInfo =
        countryOfCitizenship !== '' && countryOfResidence !== ''
      const hasSSOandSIG = params.has('sso') && params.has('sig')

      if (hasSSOandSIG) {
        await fetchDiscourseRedirectUrl(sso, sig, returnUrl)

        if (hasCountryInfo) {
          if (discourseUrl.current) {
            window.location.replace(discourseUrl.current)
          } else {
            navigate(from ?? { pathname: landingPath }, { replace: true })
          }
          return
        } else {
          setShowCountryInformationForm(true)
          return
        }
      }

      navigate(from ?? { pathname: landingPath }, { replace: true })
    } else {
      isEmailValid &&
        setLoginError(
          t('Login.Error', 'We do not recognize the email or password.')
        )
    }
  }

  const [authenticationInfo, setAuthenticationInfo] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
  })

  const handleAuthInfoUpdate = (id: string, value: string) => {
    setAuthenticationInfo({
      ...authenticationInfo,
      [id]: value,
    })
  }

  if (redirectingToInteraction) {
    return <RedirectingSkeleton />
  }

  if (showCountryInformationForm) {
    const navigateTo = () => {
      if (!!discourseUrl.current) {
        window.location.replace(discourseUrl.current)
        return
      }
      navigate(from ?? { pathname: defaultAccountPath }, { replace: true })
    }

    const logoutHandler = async () => {
      await auth.signout()
      navigate({ pathname: '/login' })
    }

    return (
      <CountryInformation handleNext={navigateTo} handleBack={logoutHandler} />
    )
  }

  return !isInitialized ? (
    isHandoff ? (
      <RedirectingSkeleton />
    ) : (
      <LoginSkeleton />
    )
  ) : (
    <>
      {location.pathname === '/login' ? (
        isLoggedIn ? (
          <RedirectingSkeleton />
        ) : (
          <AuthFormCard
            authenticationInfo={authenticationInfo}
            handleAuthInfoUpdate={handleAuthInfoUpdate}
            handleClick={handleLogin}
            errorMessage={loginError}
            hasChangedPassword={hasChangedPassword}
            name={AuthFormCard.name}
          />
        )
      ) : (
        <Outlet />
      )}
    </>
  )
}

export default Login
